<template>
  <v-app>
    <!-- Enquanto a sessao nao foi verificada, nao pisca nem login nem dashboard. -->
    <LoginView v-if="authChecked && !user" @authenticated="onAuthenticated" />
    <template v-else-if="authChecked">
    <v-main>
      <div class="app-page">
        <header class="site-header">
          <div class="site-header-inner">
            <div class="brand-lockup">
              <LuckLogo />
              <span class="brand-lockup-divider"></span>
              <NexiaLogo />
            </div>
            <div class="header-right">
              <div
                v-if="ambienteExibido"
                class="env-badge"
                :class="`env-badge--${ambienteExibido}`"
                :title="
                  selectedAgent === 'azul'
                    ? 'Ambiente da Azul — tem interruptor próprio, independente da CVC. Decidido no servidor, não aqui.'
                    : 'Ambiente ativo agora — produção ou homologação é decidido no servidor, não aqui.'
                "
              >
                {{ ambienteExibido === "prod" ? "Produção" : "Homologação" }}
              </div>
              <div class="nexia-tag">
                <v-icon size="13" color="#ffffff"
                  >mdi-hexagon-multiple-outline</v-icon
                >
                Monitoramento de agentes
              </div>
              <button
                v-if="user"
                class="logout-button"
                :title="`Sair (${user.email})`"
                @click="sair"
              >
                <v-icon size="16">mdi-logout</v-icon>
                Sair
              </button>
            </div>
          </div>
          <nav class="site-nav">
            <div class="site-nav-inner">
              <button
                v-for="item in visibleNavItems"
                :key="item.key"
                class="site-nav-item"
                :class="{ 'is-active': currentView === item.key }"
                @click="currentView = item.key"
              >
                <v-icon size="17">{{ item.icon }}</v-icon>
                {{ item.label }}
              </button>
            </div>
          </nav>
        </header>

        <div class="app-body">
          <div class="app-controls">
            <div class="filters-row">
              <div class="agent-picker">
                <span class="agent-picker-label">
                  <v-icon size="15" color="#8a929b"
                    >mdi-account-tie-outline</v-icon
                  >
                  Agente
                </span>
                <div class="agent-picker-options">
                  <button
                    v-for="a in agentOptions"
                    :key="a.key"
                    class="agent-pill"
                    :class="{
                      'is-active': selectedAgent === a.key,
                      'is-empty': !a.expandable,
                    }"
                    @click="selectedAgent = a.key"
                  >
                    <v-icon size="16">{{ a.icon }}</v-icon>
                    {{ a.name }}
                  </button>
                </div>
              </div>

              <span v-if="hasBasePicker" class="filters-row-sep" />

              <!-- Base so faz sentido pro CVC hoje (Azul/Decolar nao tem dados
                   de jeito nenhum, base ou nao). -->
              <div v-if="hasBasePicker" class="agent-picker">
                <span class="agent-picker-label">
                  <v-icon size="15" color="#8a929b">mdi-map-marker-outline</v-icon>
                  Base
                </span>
                <div class="agent-picker-options">
                  <button
                    v-for="b in baseOptions"
                    :key="b.slug"
                    class="agent-pill"
                    :class="{
                      'is-active': selectedBase === b.slug,
                      'is-empty': selectedAgent === 'cvc' && !b.hasCvc,
                    }"
                    @click="selectedBase = b.slug"
                  >
                    {{ b.label }}
                  </button>
                </div>
              </div>

              <span class="filters-row-sep" />

              <PeriodFilter :label="periodLabel" @change="handlePeriodChange" />
            </div>
          </div>

          <EmptyState
            v-if="!selectedAgentReady"
            icon="mdi-cloud-off-outline"
            :title="`Sem dados para ${selectedAgentName}`"
            text="Esse agente ainda não tem execuções registradas. Só o CVC está em operação por enquanto."
          />
          <AzulImportDetail
            v-else-if="selectedAgent === 'azul' && currentView === 'import'"
            :range="range"
            :agent-name="selectedAgentName"
            :base="selectedBase"
          />
          <AzulPendenciesDetail
            v-else-if="selectedAgent === 'azul' && currentView === 'pendencies'"
            :range="range"
            :agent-name="selectedAgentName"
            :base="selectedBase"
          />
          <AzulRespostas
            v-else-if="selectedAgent === 'azul' && currentView === 'respostas'"
            :base="selectedBase"
          />
          <AzulDashboard
            v-else-if="selectedAgent === 'azul'"
            :range="range"
            :agent-name="selectedAgentName"
            :base="selectedBase"
            :base-label="selectedBaseName"
          />
          <EmptyState
            v-else-if="!selectedBaseReady"
            icon="mdi-map-marker-off-outline"
            :title="`Sem dados para ${selectedBaseName}`"
            text="Essa base ainda não tem fornecedor CVC cadastrado no portal — o agente não roda pra ela."
          />
          <keep-alive v-else>
            <component
              :is="activeComponent"
              :range="range"
              :agent="selectedAgent"
              :agent-name="selectedAgentName"
              :base="selectedBase"
            />
          </keep-alive>
        </div>

        <footer class="site-footer">
          <div class="site-footer-inner">
            <svg
              class="site-footer-mark"
              viewBox="0 0 100 100"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <g
                v-for="angle in [0, 90, 180, 270]"
                :key="angle"
                :transform="`translate(50,50) rotate(${angle})`"
              >
                <path
                  fill-rule="evenodd"
                  fill="currentColor"
                  d="M -18,-16 A 18,18 0 1,0 18,-16 A 18,18 0 1,0 -18,-16 Z
                     M -7,-16 A 7,7 0 1,0 7,-16 A 7,7 0 1,0 -7,-16 Z"
                />
              </g>
            </svg>
            <span class="site-footer-text">Nexia &amp; Luck Receptivo</span>
            <span class="site-footer-sep">·</span>
            <span class="site-footer-text site-footer-text--muted"
              >Monitoramento de agentes</span
            >
          </div>
        </footer>
      </div>
    </v-main>
    </template>
  </v-app>
</template>

<script>
import { getPeriodRange, getPeriodLabel } from "@/data/mockData.js";
import {
  getAgents,
  getBases,
  me,
  logout,
  getCurrentEnv,
} from "@/services/nexiaApi.js";
import { getAzulEnv } from "@/services/azulApi.js";
import LoginView from "@/views/Login/Login.vue";
import LuckLogo from "@/components/LuckLogo/LuckLogo.vue";
import NexiaLogo from "@/components/NexiaLogo/NexiaLogo.vue";
import EmptyState from "@/components/EmptyState/EmptyState.vue";
import PeriodFilter from "@/views/Dashboard/Partials/PeriodFilter/PeriodFilter.vue";
import Dashboard from "@/views/Dashboard/Dashboard.vue";
import AzulDashboard from "@/views/Azul/AzulDashboard.vue";
import AzulImportDetail from "@/views/Azul/AzulImportDetail.vue";
import AzulPendenciesDetail from "@/views/Azul/AzulPendenciesDetail.vue";
import AzulRespostas from "@/views/Azul/AzulRespostas.vue";
import ImportDetail from "@/views/ImportDetail/ImportDetail.vue";
import PendenciesDetail from "@/views/PendenciesDetail/PendenciesDetail.vue";

const VIEW_COMPONENT = {
  dashboard: "Dashboard",
  import: "ImportDetail",
  pendencies: "PendenciesDetail",
};

const FALLBACK_AGENTS = [
  { key: "cvc", name: "CVC", icon: "mdi-airplane-takeoff", expandable: true },
  { key: "azul", name: "Azul", icon: "mdi-airplane", expandable: false },
  {
    key: "decolar",
    name: "Decolar",
    icon: "mdi-airplane-marker",
    expandable: false,
  },
];

const FALLBACK_BASES = [
  { slug: "recife", label: "Recife", hasCvc: true },
  { slug: "natal", label: "Natal", hasCvc: true },
  { slug: "joao_pessoa", label: "João Pessoa", hasCvc: true },
  { slug: "noronha", label: "Noronha", hasCvc: true },
  { slug: "maceio", label: "Maceió", hasCvc: false },
  { slug: "salvador", label: "Salvador", hasCvc: false },
  { slug: "aracaju", label: "Aracaju", hasCvc: false },
];

export default {
  name: "App",
  components: {
    LoginView,
    LuckLogo,
    NexiaLogo,
    EmptyState,
    PeriodFilter,
    Dashboard,
    AzulDashboard,
    AzulImportDetail,
    AzulPendenciesDetail,
    AzulRespostas,
    ImportDetail,
    PendenciesDetail,
  },
  data() {
    return {
      // authChecked evita o flash de tela: so decidimos entre login e dashboard
      // depois que /auth/me respondeu.
      authChecked: false,
      user: null,
      // So exibicao (selo no cabecalho) -- vem de GET /current-env, nunca
      // escolhido aqui. null enquanto ainda nao carregou.
      ambienteAtual: null,
      // Ambiente da AZUL (interruptor proprio): a Azul pode estar em hml enquanto a CVC segue em producao.
      ambienteAzul: null,
      currentView: "dashboard",
      periodKey: "hoje",
      customStart: null,
      customEnd: null,
      agents: [],
      // Sempre começa com um agente definido — nunca fica "sem agente".
      selectedAgent: "cvc",
      bases: [],
      // Recife e a base "de sempre" — mesmo default do backend (normalizeBase).
      selectedBase: "recife",
      navItems: [
        {
          key: "dashboard",
          label: "Dashboard",
          icon: "mdi-view-dashboard-outline",
        },
        { key: "import", label: "Importação", icon: "mdi-tray-arrow-down" },
        {
          key: "pendencies",
          label: "Detalhamento",
          icon: "mdi-clipboard-alert-outline",
        },
      ],
    };
  },
  computed: {
    // O selo mostra o ambiente do agente que esta na tela.
    ambienteExibido() {
      return this.selectedAgent === "azul" ? this.ambienteAzul : this.ambienteAtual;
    },
    range() {
      return getPeriodRange(this.periodKey, this.customStart, this.customEnd);
    },
    // Sem os botoes de preset (Hoje/Ontem/7d/30d), o botao unico do calendario
    // precisa mostrar sempre o periodo atual -- comeca em "hoje" por default.
    periodLabel() {
      return getPeriodLabel(this.periodKey, this.range);
    },
    activeComponent() {
      return VIEW_COMPONENT[this.currentView] || "Dashboard";
    },
    // Base so faz sentido para quem tem agente por base (CVC e Azul).
    hasBasePicker() {
      return this.selectedAgent === "cvc" || this.selectedAgent === "azul";
    },
    // CVC e Azul tem as mesmas 3 abas (Dashboard, Importacao, Detalhamento).
    // A aba "Respostas" (e-mails da Azul aos chamados do agente) so existe para a Azul.
    visibleNavItems() {
      if (this.selectedAgent !== "azul") return this.navItems;
      return [
        ...this.navItems,
        { key: "respostas", label: "Respostas da Azul", icon: "mdi-email-check-outline" },
      ];
    },
    agentOptions() {
      return this.agents.length ? this.agents : FALLBACK_AGENTS;
    },
    selectedAgentName() {
      const a = this.agentOptions.find((x) => x.key === this.selectedAgent);
      return a ? a.name : this.selectedAgent;
    },
    selectedAgentReady() {
      const a = this.agentOptions.find((x) => x.key === this.selectedAgent);
      return !!(a && a.expandable);
    },
    baseOptions() {
      return this.bases.length ? this.bases : FALLBACK_BASES;
    },
    selectedBaseName() {
      const b = this.baseOptions.find((x) => x.slug === this.selectedBase);
      return b ? b.label : this.selectedBase;
    },
    // Fora do CVC, base nao entra na conta — quem decide "tem dado ou nao" e
    // so o agente (Azul/Decolar ja caem no gate de selectedAgentReady).
    selectedBaseReady() {
      if (this.selectedAgent !== "cvc") return true;
      const b = this.baseOptions.find((x) => x.slug === this.selectedBase);
      return !!(b && b.hasCvc);
    },
  },
  watch: {
    // Abriu a aba da Azul sem o selo carregado (a 1a tentativa falhou): tenta de novo.
    selectedAgent(agente) {
      if (agente === "azul" && this.user && !this.ambienteAzul) this.carregarAmbienteAzul();
    },
  },
  async created() {
    this.carregarAmbienteAtual();
    // A sessao vem primeiro: sem ela as chamadas de dados tomam 401 e a tela
    // apareceria vazia antes mesmo do login.
    await this.verificarSessao();
    // O selo da Azul (/azul/env) exige sessao: a 1a chamada, acima, corre em paralelo com a verificacao e pode tomar 401 em silencio.
    if (this.user) { this.carregarAmbienteAzul(); await Promise.all([this.carregarAgentes(), this.carregarBases()]); }

    // Sessao expirada durante o uso: o interceptor do axios avisa e voltamos
    // para o login em vez de deixar a tela com dados pela metade.
    window.addEventListener("nexia:unauthenticated", this.onUnauthenticated);
  },
  beforeDestroy() {
    window.removeEventListener("nexia:unauthenticated", this.onUnauthenticated);
  },
  methods: {
    // So exibicao -- falha em silencio (o selo simplesmente nao aparece) pra
    // nao travar o resto da tela por causa de um detalhe cosmetico.
    async carregarAmbienteAtual() {
      try {
        this.ambienteAtual = await getCurrentEnv();
      } catch (err) {
        console.error("Falha ao carregar ambiente atual", err);
      }
      await this.carregarAmbienteAzul();
    },
    // Ambiente da Azul (interruptor proprio). Exige sessao; falha em silencio (o selo nao aparece) e e' repetida apos o login e ao abrir a aba da Azul.
    async carregarAmbienteAzul() {
      try {
        this.ambienteAzul = await getAzulEnv();
      } catch (err) {
        console.error("Falha ao carregar ambiente da Azul", err);
      }
    },
    async carregarAgentes() {
      try {
        this.agents = await getAgents();
      } catch (err) {
        console.error("Falha ao carregar agentes", err);
        this.agents = [];
      }
      // Garante que sempre há um agente válido selecionado.
      if (!this.agentOptions.some((a) => a.key === this.selectedAgent)) {
        this.selectedAgent = (this.agentOptions[0] || { key: "cvc" }).key;
      }
    },
    async carregarBases() {
      try {
        this.bases = await getBases();
      } catch (err) {
        console.error("Falha ao carregar bases", err);
        this.bases = [];
      }
      if (!this.baseOptions.some((b) => b.slug === this.selectedBase)) {
        this.selectedBase = (this.baseOptions[0] || { slug: "recife" }).slug;
      }
    },
    onUnauthenticated() {
      this.user = null;
    },
    async verificarSessao() {
      this.user = await me();
      this.authChecked = true;
    },
    onAuthenticated(user) {
      this.user = user;
      // O dashboard so carrega dados depois do login; sem isto a primeira tela
      // viria vazia porque as chamadas do mounted() tomaram 401.
      this.carregarAgentes();
      this.carregarBases();
      this.carregarAmbienteAzul();
    },
    async sair() {
      try {
        await logout();
      } finally {
        this.user = null;
      }
    },
    handlePeriodChange({ key, start, end }) {
      this.periodKey = key;
      this.customStart = start;
      this.customEnd = end;
    },
  },
};
</script>

<style lang="scss">
.v-application {
  background: #f4f6f8 !important;
}

.app-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.site-header {
  background: linear-gradient(135deg, #12897a 0%, #0a5c53 100%);
  box-shadow: 0 2px 10px rgba(10, 60, 55, 0.18);

  &-inner {
    max-width: 1300px;
    margin: 0 auto;
    padding: 18px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
  }
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: 16px;

  // A marca da Luck e verde+vermelha; no cabecalho verde-escuro ela vai numa
  // pilula branca pra manter as cores legiveis. No login (fundo claro) nao.
  .luck-logo {
    background: #ffffff;
    border-radius: 9px;
    padding: 6px 12px;
  }

  &-divider {
    width: 1px;
    align-self: stretch;
    min-height: 44px;
    background: rgba(255, 255, 255, 0.28);
  }
}

.nexia-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 20px;
  padding: 6px 12px;
}

.site-nav {
  background: rgba(4, 40, 36, 0.28);
  border-top: 1px solid rgba(255, 255, 255, 0.12);

  &-inner {
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  &-item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    padding: 13px 16px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

    .v-icon {
      color: inherit;
    }

    &:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.06);
    }

    &.is-active {
      color: #ffffff;
      border-bottom-color: #ffffff;
    }
  }
}

.app-body {
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 26px 32px 60px;
  flex: 1 0 auto;
}

.app-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e6e9ec;
}

// Agente + Base sempre na mesma linha. Com CVC + 7 bases a largura combinada
// passa do container em telas normais -- em vez de quebrar linha (o que
// fazia a Base cair pra baixo do Agente), deixa rolar na horizontal.
.filters-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 4px; // respiro pra barra de rolagem nao colar nas pills

  .agent-picker {
    flex: 0 0 auto;
  }

  &-sep {
    width: 1px;
    flex: 0 0 auto;
    align-self: stretch;
    min-height: 24px;
    background: #e6e9ec;
  }
}

.agent-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  &-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #8a929b;
  }

  &-options {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
  }
}

.agent-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  white-space: nowrap;
  border: 1px solid #d9dde1;
  background: #fff;
  color: #4b535c;
  font-size: 0.87rem;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 9px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  .v-icon {
    color: #8a929b;
  }

  &:hover {
    border-color: #12897a;
  }

  &.is-active {
    background: #0f7a6e;
    border-color: #0f7a6e;
    color: #fff;

    .v-icon {
      color: #fff;
    }
  }

  &.is-empty {
    color: #b0b6bc;
    background: #f4f5f6;
    border-color: #e6e9ec;
    border-style: dashed;

    .v-icon {
      color: #c3c8cd;
    }

    &:hover {
      border-color: #c3c8cd;
    }

    &.is-active {
      background: #b0b6bc;
      border-color: #b0b6bc;
      border-style: solid;
      color: #fff;

      .v-icon {
        color: #fff;
      }
    }
  }

}

.site-footer {
  border-top: 1px solid #e6e9ec;
  background: #ffffff;

  &-inner {
    max-width: 1300px;
    margin: 0 auto;
    padding: 18px 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &-mark {
    color: #0f7a6e;
  }

  &-text {
    font-size: 0.78rem;
    font-weight: 700;
    color: #384049;

    &--muted {
      font-weight: 400;
      color: #9aa2ab;
    }
  }

  &-sep {
    color: #c7ccd1;
    font-size: 0.78rem;
  }
}

@media (max-width: 600px) {
  .site-header-inner {
    padding: 16px 18px;
  }

  .site-nav-inner {
    padding: 0 8px;
  }

  .site-nav-item {
    padding: 12px 10px;
    font-size: 0.78rem;
  }

  .app-body {
    padding: 18px 14px 40px;
  }

  .site-footer-inner {
    padding: 16px 18px;
    text-align: center;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
// Selo de ambiente: so leitura, nunca clicavel. Homologacao em ambar pra
// nunca confundir dado de teste com dado real numa olhada rapida.
.env-badge {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0b5c52;
  background: #ffffff;
}
.env-badge--hml {
  color: #6a4300;
  background: #ffc857;
  border-color: transparent;
}

.logout-button {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.logout-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}
</style>
